import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { config } from '../../lib/config';

/**
 * TEMPORARY — password recovery for customers who have not migrated from v1 yet.
 *
 * Remove this page, its route and the link from the sign-in screen at the v1 decommission.
 *
 * The normal "forgot password" on the sign-in screen cannot help someone who does not exist in the new
 * pool yet: Cognito answers with "we sent you a code" and nothing is sent. Rather than leave those
 * customers waiting on a message that will never arrive, this drives the OLD system's own reset. Once the
 * old password is set, signing in normally migrates the account.
 */

type Step = 'email' | 'code' | 'done';

/** Error codes the handler returns, in the words the customer needs. */
const MESSAGES: Record<string, string> = {
  'no-v1-account': 'We could not find an account with that email address on the previous version of EFTCreator. Check the spelling, or try another address you may have signed up with.',
  'bad-code': 'That code does not match. Check the email again — codes are six digits.',
  'expired-code': 'That code has expired. Request a new one below.',
  'rate-limited': 'Too many attempts. Wait a few minutes and try again.',
  'not-allowed': 'That account cannot be reset here. Please contact support@lakotasoftware.com.',
  'recovery-failed': 'Something went wrong on our side. Please try again, or contact support@lakotasoftware.com.',
};

export default function V1Recovery() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [destination, setDestination] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function post(path: string, body: unknown): Promise<{ ok: boolean; data: Record<string, unknown> }> {
    const res = await fetch(`${config.apiEndpoint}/v1-recovery/${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data: Record<string, unknown> = {};
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      /* a body-less error still has a status */
    }
    return { ok: res.ok, data };
  }

  /** Turn whatever came back into something worth reading. */
  function explain(data: Record<string, unknown>): string {
    const key = String(data['error'] ?? data['message'] ?? '');
    // InvalidPasswordException arrives as Cognito's own sentence about the policy, so it is shown verbatim.
    return MESSAGES[key] ?? (key || 'Something went wrong. Please try again.');
  }

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { ok, data } = await post('start', { email });
      if (!ok) {
        setError(explain(data));
        return;
      }
      if (data['status'] === 'already-migrated') {
        /*
         * Their account is already on the new system, so the old password is not what is stopping them.
         * Sending them round this loop would change nothing — the sign-in screen's own reset is the one
         * that works for them.
         */
        setNotice('Good news — your account has already moved to the new EFTCreator. Use the normal "Forgot your password?" link on the sign-in screen instead; this page cannot help with an account that has already moved.');
        return;
      }
      setDestination((data['destination'] as string) ?? null);
      setStep('code');
    } catch {
      setError('We could not reach EFTCreator. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { ok, data } = await post('confirm', { email, code, password });
      if (!ok) {
        setError(explain(data));
        return;
      }
      setStep('done');
    } catch {
      setError('We could not reach EFTCreator. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page grain">
      <div className="auth-page-bg" />
      <div className="v1rec">
        <h1 className="v1rec-h">Reset your password</h1>
        <p className="v1rec-sub">
          For customers who have not signed in to the new EFTCreator yet. We will send a code to the email
          address on your existing account.
        </p>

        {notice && <div className="v1rec-notice">{notice}</div>}
        {error && <div className="v1rec-error">{error}</div>}

        {step === 'email' && (
          <form onSubmit={sendCode} className="v1rec-form">
            <label className="v1rec-label" htmlFor="v1rec-email">Email address</label>
            <input
              id="v1rec-email"
              className="v1rec-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder="you@yourcompany.com"
              required
            />
            <button className="v1rec-btn" type="submit" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={resetPassword} className="v1rec-form">
            <p className="v1rec-sent">
              We sent a code to <b>{destination ?? 'your email address'}</b>. It may take a minute, and it
              may land in spam.
            </p>
            <label className="v1rec-label" htmlFor="v1rec-code">Code</label>
            <input
              id="v1rec-code"
              className="v1rec-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(ev) => setCode(ev.target.value)}
              required
            />
            <label className="v1rec-label" htmlFor="v1rec-pw">New password</label>
            <input
              id="v1rec-pw"
              className="v1rec-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              required
            />
            <button className="v1rec-btn" type="submit" disabled={busy || !code || !password}>
              {busy ? 'Setting password…' : 'Set password'}
            </button>
            <button className="v1rec-link" type="button" onClick={() => sendCode()} disabled={busy}>
              Send a new code
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="v1rec-form">
            <p className="v1rec-sent">
              Your password is set. Sign in with <b>{email}</b> and your new password — your account and its
              history will move across automatically the first time you do.
            </p>
            <button className="v1rec-btn" type="button" onClick={() => navigate('/app')}>
              Go to sign in
            </button>
          </div>
        )}

        <Link className="v1rec-back" to="/app">Back to sign in</Link>
      </div>
    </div>
  );
}
